import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import SubscriberNeeds from './components/SubscriberNeeds.jsx';
import MainSubscriberNeeds from './components/MainSubscriberNeeds.jsx';
import PropTypes from 'prop-types';
import { isGroupOffer, offerCodeEnum } from '@@viewEnums';
import { FormProvider } from 'react-hook-form';
import useSimulationNeedsForm from './hooks/useSimulationNeedsForm.jsx';
import { Icon, PageSubtitle, PageTitle, Tabs } from '@fasstech/middle-design-system';
import { equals, not, pathOr, propOr } from 'ramda';
import { useTabs } from '@@viewCommonComponents/hooks/useTabs.js';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import SimulationNeedsGroupHealth from './components/SimulationNeedsGroupHealth';
import SimulationNeedsExtraForm from './extra/SimulationNeedsExtraForm.jsx';
import GetSimulationNeeds from './helpers/GetSimulationNeeds.jsx';
import SimulationNeedsLayout from './helpers/SimulationNeedsLayout.jsx';
import SimulationNeedsSelfEmployed from '@@views/simulationNeeds/client/components/SimulationNeedsSelfEmployed.jsx';

const SimulationNeedsForm = ({
  getSimulationNeedsSubscribers,
  getSimulationNeeds,
  getMiddleText,
  getMiddleBoolean,
  middleFeatures,
  viewMode
}) => {
  const {
    tabs,
    setTabs,
    updateVisibility,
    isButtonDisabled,
    selectedGrantee,
    setSelectedGrantee,
    displayTabsPagination
  } = useTabs({
    grantees: propOr([], 'subscribers', getSimulationNeedsSubscribers),
    getMiddleText,
    filteredViewMode: equals(viewMode, 'CUSTOMER')
  });
  const {
    onSubmit,
    levels,
    postes,
    subscribers,
    selectedSubscriberIsMainSubscriber,
    $onClickPrev,
    mainSubscriber,
    offerCode,
    form,
    isValid,
    subscriberIndex,
    typeSouhait,
    areAllLevelsFilled,
    collectionNeedChoices
  } = useSimulationNeedsForm({
    getSimulationNeedsSubscribers,
    getSimulationNeeds,
    getMiddleText,
    getMiddleBoolean,
    middleFeatures,
    tabs,
    updateVisibility,
    isButtonDisabled,
    selectedGrantee,
    viewMode
  });
  const enforcedGuaranteeGroupHealth = form.watch(
    'collectionNeeds.0.isEnforcedGuarantee'
  );
  const displayIsEnforcedGuaranteeGroupHealth = propOr(
    false,
    'displayIsEnforcedGuarantee',
    getSimulationNeeds
  );
  const companyCcnHasAgreement = pathOr(
    false,
    [ 'companies', 0, 'ccnHasAgreement' ],
    getSimulationNeeds
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <SimulationNeedsLayout>
          <Icon name={'collection-need'} />
          <PageTitle>
            {equals(0, tabs.selectedTab)
              ? getMiddleText('RECUEIL_BESOIN_VIEW_TITLE')
              : getMiddleText('BENEFICIAIRE_LABEL_GENERAL_NEEDS_TITLE')}
          </PageTitle>
          {(offerCode === offerCodeEnum.INDIVIDUAL_HEALTH ||
            isGroupOffer(offerCode)) && (
            <>
              <PageSubtitle className={'mb-0 text-lg text-center'}>
                {getMiddleText('RECUEIL_BESOIN_VIEW_DESCRIPTION')}
              </PageSubtitle>

              {isGroupOffer(offerCode) && (
                <SimulationNeedsGroupHealth
                  getMiddleText={getMiddleText}
                  getMiddleBoolean={getMiddleBoolean}
                  form={form}
                  displayIsEnforcedGuarantee={
                    displayIsEnforcedGuaranteeGroupHealth
                  }
                  companyCcnHasAgreement={companyCcnHasAgreement}
                />
              )}

              <div className={'flex flex-col gap-4'}>
                <TabsPagination
                  updateVisibility={updateVisibility}
                  isButtonDisabled={isButtonDisabled}
                  show={displayTabsPagination}
                >
                  <Tabs
                    items={tabs.tabs}
                    selected={tabs.selectedTab}
                    onClick={(index) => {
                      setTabs({
                        selectedTab: index,
                        tabs: tabs.tabs
                      });
                      const grantee = subscribers[index];
                      if (grantee) {
                        setSelectedGrantee(grantee);
                      }
                    }}
                    isDisplayed={tabs.tabs.length > 1}
                  />
                </TabsPagination>
              </div>
              {offerCode !== offerCodeEnum.GROUP_PROTECTION &&
                (offerCode === offerCodeEnum.INDIVIDUAL_HEALTH ||
                !typeSouhait ||
                enforcedGuaranteeGroupHealth ||
                !displayIsEnforcedGuaranteeGroupHealth ||
                !companyCcnHasAgreement) && (
                <MainSubscriberNeeds
                  selectedSubscriberIsMainSubscriber={
                    selectedSubscriberIsMainSubscriber
                  }
                  setSelectedGrantee={setSelectedGrantee}
                  mainSubscriber={mainSubscriber}
                  selectedTab={tabs.selectedTab}
                >
                  {selectedSubscriberIsMainSubscriber && (
                    <GetSimulationNeeds
                      form={form}
                      name={`subscriberNeeds.${subscriberIndex}.healthNeeds`}
                      postes={postes}
                      levels={levels}
                    />
                  )}
                </MainSubscriberNeeds>
              )}

              {offerCode !== offerCodeEnum.GROUP_PROTECTION &&
                viewMode === 'CUSTOMER_GRANTEES' && (
                <div className={'w-full'}>
                  {!selectedSubscriberIsMainSubscriber && (
                    <SubscriberNeeds selectedTab={tabs.selectedTab}>
                      <GetSimulationNeeds
                        form={form}
                        name={`subscriberNeeds.${subscriberIndex}.healthNeeds`}
                        postes={postes}
                        levels={levels}
                      />
                    </SubscriberNeeds>
                  )}
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-1 w-full">
            {offerCode === offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION && (
              <SimulationNeedsSelfEmployed
                getMiddleText={getMiddleText}
                getMiddleBoolean={getMiddleBoolean}
                form={form}
                collectionNeedChoices={collectionNeedChoices}
              />
            )}
            <SimulationNeedsExtraForm form={form} />
          </div>
        </SimulationNeedsLayout>
        <ButtonContainer
          getMiddleText={getMiddleText}
          $onPreviousButtonClick={$onClickPrev}
          nextButtonLabel={getMiddleText(
            'GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL'
          )}
          displayResumeButton
          isFormError={not(isValid) || !areAllLevelsFilled}
          nextButtonIsDisabled={not(isValid) || !areAllLevelsFilled}
        />
      </form>
    </FormProvider>
  );
};

SimulationNeedsForm.propTypes = {
  getMiddleText: PropTypes.func
};

export default SimulationNeedsForm;
